<template>
  <div>
    <vue-headful :title="this.setPageTitle('scoringProfileCreate')" />
    <loading :active="getLoading" :color="'blue'" is-full-page loader="spinner"> </loading>

    <b-container class="my-4">
      <!-- HEADER -->
      <custom-flex-header :parent-node-class="['my-4']">
        <template v-slot:left-content>
          <h1 class="mb-0 h4 font-weight-normal">
            {{ $t('scoringProfileDetails.title') }}
          </h1>
        </template>
        <template v-slot:right-content>
          <b-button size="sm" variant="outline-primary" class="mr-1" @click="cancelEditingProfile">
            {{ $t('common.cancel') }}
          </b-button>
          <b-button size="sm" variant="primary" @click="saveScoringProfile">
            {{ $t('common.save') }}
          </b-button>
        </template>
      </custom-flex-header>

      <!-- NAME + DESCRIPTION -->
      <b-row class="mb-4">
        <b-col>
          <div class="d-flex">
            <div class="mr-2 w-50">
              <span>{{ $t('common.name') }}</span>
              <b-input
                size="sm"
                type="text"
                autocomplete="off"
                :formatter="nameFormatter"
                v-model="scoringProfileName"
                :placeholder="$t('scoringProfileDetails.name')"
              ></b-input>
            </div>
            <div class="w-50">
              <span>{{ $t('common.description') }}</span>
              <b-input
                size="sm"
                type="text"
                autocomplete="off"
                :formatter="descriptionFormatter"
                v-model="scoringProfileDescription"
                :placeholder="$t('scoringProfileDetails.description')"
              ></b-input>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- PROFILE COVERAGE -->
      <b-row class="mb-4">
        <b-col>
          <profile-coverage :dataCoverage="getProfileCoverage"></profile-coverage>
        </b-col>
      </b-row>

      <!-- TABS -->
      <b-row class="mb-4">
        <b-col>
          <b-tabs
            v-model="currentTab"
            class="fairmat-primary-tabs"
            @activate-tab="onActivateTab"
            data-test="tabsScoringDefinition"
          >
            <b-tab :title="$t('scoringProfileDetails.filters')">
              <div class="scoring-tab-box">
                <p class="scoring-tab-hint">{{ $t('scoringProfileDetails.hints.filters') }}</p>
                <scoring-profile-filters-panel></scoring-profile-filters-panel>
              </div>
            </b-tab>
            <b-tab :title="$t('scoringProfileDetails.profileCoverageDetail')">
              <div class="scoring-tab-box">
                <p class="scoring-tab-hint">{{ $t('scoringProfileDetails.hints.coverage') }}</p>
                <tab-profile-coverage></tab-profile-coverage>
              </div>
            </b-tab>
            <b-tab :title="$t('scoringProfileDetails.scoringDefinition')">
              <div class="scoring-tab-box">
                <p class="scoring-tab-hint">{{ $t('scoringProfileDetails.hints.scoringDefinition') }}</p>
                <tab-scoring-definition></tab-scoring-definition>
              </div>
            </b-tab>
          </b-tabs>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
// 3-tab shell: the filters tab uses the catalog-driven ScoringProfileFiltersPanel and the save call
// goes to the catalog-native /api/scoringProfiles/save endpoint. TabProfileCoverage/TabScoringDefinition
// are not part of the filter catalog.
import TabProfileCoverage from './TabProfileCoverage.vue'
import TabScoringDefinition from './TabScoringDefinition.vue'
import CustomFlexHeader from '../../components/Lib/CustomFlexHeader.vue'
import ProfileCoverage from '../../components/ScoringProfiles/ProfileCoverage.vue'
import ScoringProfileFiltersPanel from '../../components/ScoringProfiles/ScoringProfileFiltersPanel.vue'
import unsavedChangesMixin from '../../mixins/unsavedChanges'
import { scoringProfileSnapshot } from '../../utils/unsavedChangesSnapshots'

export default {
  mixins: [unsavedChangesMixin],

  components: {
    CustomFlexHeader,
    TabProfileCoverage,
    TabScoringDefinition,
    ProfileCoverage,
    ScoringProfileFiltersPanel,
  },

  data() {
    return {
      currentTab: 0,
      maxProfileNameLength: 128,
      maxProfileDescriptionLength: 255,
    }
  },

  computed: {
    getLoading: {
      get() {
        return this.$store.getters['scoringProfilesCommon/getLoading']
      },
    },
    scoringProfileName: {
      get() {
        return this.$store.getters['scoringProfilesCommon/getScoringProfileName']
      },
      set(newVal) {
        this.$store.dispatch('scoringProfilesCommon/setScoringProfileName', this.nameFormatter(newVal))
      },
    },
    scoringProfileDescription: {
      get() {
        return this.$store.getters['scoringProfilesCommon/getScoringProfileDescription']
      },
      set(newVal) {
        this.$store.dispatch('scoringProfilesCommon/setScoringProfileDescription', this.descriptionFormatter(newVal))
      },
    },
    getProfileCoverage() {
      return this.$store.getters['scoringProfilesCommon/getProfileCoverage']
    },
  },

  methods: {
    unsavedChangesSnapshot() {
      return scoringProfileSnapshot(this)
    },

    unsavedChangesReady() {
      return !this.getLoading
    },

    nameFormatter(value) {
      return value.substring(0, this.maxProfileNameLength)
    },
    descriptionFormatter(value) {
      return value.substring(0, this.maxProfileDescriptionLength)
    },
    saveScoringProfile() {
      const isValidName = this.$store.getters['scoringProfilesCommon/getScoringProfileNameValidation']

      if (!isValidName) {
        this.$bvToast.toast(this.$t('scoringProfiles.errorOnMissingName'), {
          title: this.$t('scoringProfiles.scoringDefinition'),
          variant: 'danger',
        })
        return
      }

      const results = this.$store.getters['scoringProfilesCommon/getScoringProfileDefinitionValidation']
      const modelId = this.$store.getters['scoringProfilesDefinition/getSelectedModelId']
      const driverFamiliesList = this.$store.getters['scoringProfilesDefinition/getUnsortedModelDrivers']
      const sortedDrivers = this.$store.getters['scoringProfilesDefinition/getSortedModelDrivers']

      // if model selected NO need to validate scoring definition
      if (!modelId) {
        if (!results.isValidScoringDefinition) {
          this.$bvToast.toast(this.$t('scoringProfiles.errorOnDriversSelected'), {
            title: this.$t('scoringProfiles.scoringDefinition'),
            variant: 'danger',
          })
        }

        if (results.isValidScoringDefinition && !results.isValidScoringDefinitionTotalWeight) {
          this.$bvToast.toast(this.$t('scoringProfiles.errorOnTotalWeight'), {
            title: this.$t('scoringProfiles.scoringDefinition'),
            variant: 'danger',
          })
        }

        if (!results.isDriversWeightsGreaterThanZero) {
          this.$bvToast.toast(this.$t('scoringProfiles.weightsMustBeGreaterThan0'), {
            title: this.$t('scoringProfiles.scoringDefinition'),
            variant: 'danger',
          })
        }
      } else {
        if (driverFamiliesList.length !== sortedDrivers.length) {
          this.$bvToast.toast(this.$t('scoringProfiles.allDriversMustBeSelectedAndSorted'), {
            title: this.$t('scoringProfiles.scoringDefinition'),
            variant: 'danger',
          })
        }
      }

      const isValid = modelId
        ? driverFamiliesList.length === sortedDrivers.length
        : Object.values(results).every((j) => j !== false)

      if (isValid) {
        this.$store
          .dispatch('scoringProfileEditor/saveScoringProfile')
          .then(() => {
            this.$router.push({ name: 'scoringProfilesList' })
          })
          .catch(async (error) => {
            const err = await error.response
            const message = await err.text()
            if (message.includes('NameInUse')) {
              this.$bvToast.toast(this.$t('scoringProfiles.nameInUse'), {
                title: this.$t('scoringProfiles.title'),
                variant: 'danger',
              })
            } else if (message.includes('ScoringDefinitionWeightInvalid')) {
              this.$bvToast.toast(this.$t('scoringProfiles.scoringDefinitionWeightInvalid'), {
                title: this.$t('scoringProfiles.title'),
                variant: 'danger',
              })
            } else if (message.includes('DriversEmpty')) {
              this.$bvToast.toast(this.$t('scoringProfiles.errorOnDriversSelected'), {
                title: this.$t('scoringProfiles.title'),
                variant: 'danger',
              })
            } else {
              this.$bvModal.msgBoxOk(this.$t('common.genericError'))
            }
          })
      }
    },
    cancelEditingProfile() {
      this.$bvModal
        .msgBoxConfirm(this.$t('scoringProfilesList.modalMessages.confirmUnsaved'), {
          cancelTitle: this.$t('common.no'),
          okTitle: this.$t('common.yes'),
        })
        .then((value) => {
          // OK = value = true
          if (value) {
            this.$router.push({ name: 'scoringProfilesList' })
          }
        })
    },
    onActivateTab(newTabIndex, prevTabIndex, firedEvent) {
      if (newTabIndex === 1) {
        this.$store.dispatch('scoringProfileEditor/loadProductsList', null).catch((err) => {
          this.httpErrorsHandler(err)
        })
      }
    },
  },

  created() {
    this.$store
      .dispatch('scoringProfilesModelsList/loadModelsListToAssociate')
      .then(() => {
        this.$store.dispatch('scoringProfilesCommon/newScoringProfile')

        // "+ Crea un nuovo profilo per questo prodotto" (Benchmarking.vue) passes the preset
        // computed from the product as this.$route.params.filters ([{key, value}], same shape as
        // filterCollection) — apply it instead of resetting when present.
        const presetFilters = this.$route.params.filters
        if (Array.isArray(presetFilters) && presetFilters.length > 0) {
          this.$store.dispatch('scoringProfilesFilters/setFilters', presetFilters)
        } else {
          this.$store.dispatch('scoringProfilesFilters/resetFilters')
        }
      })
      .catch((err) => {
        this.httpErrorsHandler(err)
      })
  },
}
</script>

<style lang="scss" scoped></style>
